/** Seed data for the Supplier Onboarding review queue (Demo Mode only, local-only - no
 * backend persistence yet). Distinct from the "quick refer a supplier" banner on the
 * Suppliers list (see `DemoScenarioContext.tsx`) - this models the fuller onboarding
 * request/review/decision flow described in the demo spec (duplicate check, existing
 * alternatives, required documents, commercial/compliance review, activation scope). */

export type OnboardingRequestStatus = 'Submitted' | 'UnderReview' | 'Approved' | 'Rejected';
export type OnboardingUrgency = 'Low' | 'Medium' | 'High';
export type OnboardingActivationScope = 'SiteOnly' | 'SiteGroup' | 'ClientAccount' | 'AllSites';

export interface SupplierOnboardingRequest {
  id: number;
  requestedSupplierName: string;
  requestedBy: string;
  site: string;
  clientAccount: string;
  reason: string;
  productCategoryNeeded: string;
  expectedUsage: string;
  urgency: OnboardingUrgency;
  suggestedContact?: string;
  submittedDate: string;
  status: OnboardingRequestStatus;
  duplicateSupplierName?: string;
  existingAlternatives: string[];
  requiredDocuments: string[];
  commercialReviewNotes?: string;
  complianceReviewNotes?: string;
  activationScope?: OnboardingActivationScope;
  decisionNotes?: string;
}

export const supplierOnboardingRequestsSeed: SupplierOnboardingRequest[] = [
  {
    id: 1,
    requestedSupplierName: 'Northgate Dry Goods',
    requestedBy: 'Chef Amara Osei',
    site: 'Site 0145 - Riverside House',
    clientAccount: 'Greenway Care Group',
    reason: 'New allergen-free flour range needs a dedicated dry goods supplier.',
    productCategoryNeeded: 'Bakery - dry goods',
    expectedUsage: '~40 cases/month',
    urgency: 'Medium',
    suggestedContact: 'sales@northgatedrygoods.example',
    submittedDate: '2026-07-05',
    status: 'Submitted',
    existingAlternatives: ['Millbrook Bakery Supplies (partial range match)'],
    requiredDocuments: ['Public liability insurance', 'Food safety certification', 'Bank details'],
  },
  {
    id: 2,
    requestedSupplierName: 'Coastal Seafood Co',
    requestedBy: 'Head Chef Daniel Ruiz',
    site: 'Site 0203 - Harbourview Lodge',
    clientAccount: 'Coastal Care Trust',
    reason: 'New seasonal seafood menu item, no approved seafood supplier on file for this site.',
    productCategoryNeeded: 'Fresh seafood',
    expectedUsage: '~15 cases/week',
    urgency: 'High',
    suggestedContact: 'orders@coastalseafood.example',
    submittedDate: '2026-06-25',
    status: 'UnderReview',
    duplicateSupplierName: undefined,
    existingAlternatives: [],
    requiredDocuments: ['Public liability insurance', 'Food safety certification', 'HACCP documents', 'Bank details'],
    commercialReviewNotes: 'Pricing broadly in line with existing seafood suppliers on other accounts - no margin concern.',
    complianceReviewNotes: 'HACCP documents requested, awaiting upload from supplier.',
  },
  {
    id: 3,
    requestedSupplierName: 'Millbrook Bakery Supplies',
    requestedBy: 'Procurement (bulk import)',
    site: 'All sites',
    clientAccount: 'Greenway Care Group',
    reason: 'Existing strategic supplier - re-onboarded after governance profile refresh.',
    productCategoryNeeded: 'Bakery & ambient',
    expectedUsage: 'Existing volume',
    urgency: 'Low',
    submittedDate: '2026-02-01',
    status: 'Approved',
    existingAlternatives: [],
    requiredDocuments: [],
    activationScope: 'ClientAccount',
    decisionNotes: 'Approved by Procurement - full governance profile already on file.',
  },
  {
    id: 4,
    requestedSupplierName: 'QuickBite Snacks Direct',
    requestedBy: 'Site Manager Ella Fox',
    site: 'Site 0088 - Meadowfield',
    clientAccount: 'Greenway Care Group',
    reason: 'One-off request for a branded snack range for a single event.',
    productCategoryNeeded: 'Snacks',
    expectedUsage: 'One-off, ~2 cases',
    urgency: 'Low',
    submittedDate: '2026-06-01',
    status: 'Rejected',
    existingAlternatives: ['Millbrook Bakery Supplies stocks a comparable snack range'],
    requiredDocuments: ['Public liability insurance'],
    decisionNotes: 'Rejected - low volume, one-off need already covered by an existing approved supplier.',
  },
];
